/**
 * RoleDashboardPage — role-aware landing screen.
 *
 * Dispatches to one of 5 layouts based on the caller's effective_role and (for FH)
 * the primary assigned site's site_type.
 *
 *  - OWNER / HEAD                       → <Navigate to="/command" />
 *  - HO_ADMIN / PROJECT_MANAGER         → SiteGridDashboard (existing HomePage-style)
 *  - FACILITY_HEAD × MANAGED_CLIENT     → ManagedClientDashboard
 *  - FACILITY_HEAD × OWN_BUILDING       → BuildingDashboard
 *  - FACILITY_HEAD × COWORKING          → CoworkingDashboard
 *  - counter_staff                       → CounterDashboard
 *  - ASSISTANT                          → AssistantDashboard
 *
 * All dashboards respect the financial lockdown — they never call gated endpoints
 * unless the caller has financial access, and gracefully skip 403s.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
    ClipboardList, Users, Package, PackageMinus, Wallet, ShoppingCart, MessageSquare,
    AlertTriangle, GraduationCap, Building2, Coffee, Printer, ArrowRight, CheckCircle2,
    Calendar,
} from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate, fmtInr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useGreeting } from "../lib/useGreeting";
import { ProgressRing } from "../components/ProgressRing";
import { StreakBadge } from "../components/StreakBadge";
import { EmptyState } from "../components/EmptyState";
import ActionsNow from "../components/ActionsNow";
import WhiteboardBanner from "../components/WhiteboardBanner";

/** Route entry-point — reads user, dispatches. */
export default function RoleDashboardPage() {
    const { user } = useAuth();
    const role = user?.effective_role;
    const isCounter = user?.capabilities?.counter_staff;
    const [sites, setSites] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/sites");
                setSites(data || []);
            } catch (e) { toast.error(apiError(e)); setSites([]); }
        })();
    }, []);

    if (role === "OWNER" || role === "HEAD") return <Navigate to="/command" replace />;

    // Procurement Officer lands directly on the procurement dashboard.
    if (role === "PROCUREMENT_OFFICER") return <Navigate to="/procurement" replace />;

    // External client lands on the read-only portal.
    if (role === "CLIENT") return <Navigate to="/client" replace />;

    // For HO_ADMIN / PROJECT_MANAGER the existing HomePage tile grid already works —
    // route them there so we don't duplicate site-list plumbing.
    if (role === "HO_ADMIN" || role === "PROJECT_MANAGER" || role === "HEAD") return <Navigate to="/" replace />;

    if (isCounter) return <CounterDashboard user={user} sites={sites} />;
    if (role === "ASSISTANT") return <AssistantDashboard user={user} sites={sites} />;

    if (!sites) return <DashboardSkeleton />;
    const primary = sites[0];
    if (!primary) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-8">
                <EmptyState icon="🔑" title="No site assigned yet" subtitle="Please ask your HO Admin to assign a site." />
            </div>
        );
    }

    if (primary.site_type === "OWN_BUILDING") return <BuildingDashboard user={user} site={primary} sites={sites} />;
    if (primary.site_type === "COWORKING")    return <CoworkingDashboard user={user} site={primary} sites={sites} />;
    return <ManagedClientDashboard user={user} site={primary} sites={sites} />;
}

/* ------------------------------ shared bits ------------------------------ */

function DashboardSkeleton() {
    return (
        <div className="mx-auto max-w-3xl px-4 py-8 space-y-4">
            <div className="h-20 rounded-2xl bg-white animate-pulse" />
            <div className="h-40 rounded-2xl bg-white animate-pulse" />
            <div className="h-32 rounded-2xl bg-white animate-pulse" />
        </div>
    );
}

function GreetingHeader({ user, subtitle }) {
    const { phrase } = useGreeting(user);
    return (
        <div className="fx-fade-in-up">
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Today · {fmtDate(new Date().toISOString())}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1" data-testid="dashboard-greeting">{phrase}</h1>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
    );
}

function QuickAction({ to, icon: Icon, label, accent, testid }) {
    return (
        <Link
            to={to}
            className="fx-tap fx-card fx-card-hover flex items-center gap-3 p-3.5"
            style={accent ? { "--accent": accent } : undefined}
            data-testid={testid}
        >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent || "#1E3355"}15`, color: accent || "#1E3355" }}>
                <Icon className="w-5 h-5" />
            </div>
            <span className="text-sm font-medium text-[#1E3355] flex-1">{label}</span>
            <ArrowRight className="w-4 h-4 text-gray-400" />
        </Link>
    );
}

/* ---------------------- Managed Client (FH) Dashboard ---------------------- */

function ManagedClientDashboard({ user, site, sites }) {
    const accent = site.accent_color || "#1E3355";
    const [run, setRun] = useState(null);
    const [streak, setStreak] = useState(null);
    const [openSRs, setOpenSRs] = useState([]);
    const [pendingReq, setPendingReq] = useState(0);
    const [roster, setRoster] = useState([]);

    useEffect(() => {
        (async () => {
            const tasks = [
                api.get(`/sites/${site.id}/runs/today`).then((r) => setRun(r.data)).catch(() => {}),
                api.get(`/sites/${site.id}/streak`).then((r) => setStreak(r.data)).catch(() => {}),
                api.get(`/service-requests`, { params: { site_id: site.id, status: "Open" } }).then((r) => setOpenSRs(r.data || [])).catch(() => {}),
                api.get(`/requisitions`, { params: { site_id: site.id, status: "SUBMITTED" } }).then((r) => setPendingReq((r.data || []).length)).catch(() => {}),
                api.get(`/sites/${site.id}/roster`).then((r) => setRoster(r.data || [])).catch(() => {}),
            ];
            await Promise.allSettled(tasks);
        })();
    }, [site.id]);

    const progress = useMemo(() => {
        if (!run?.tasks) return { pct: 0, done: 0, total: 0, critDone: 0, critTotal: 0 };
        const t = run.tasks;
        const done = t.filter((x) => x.status === "DONE").length;
        const critTotal = t.filter((x) => x.priority === "CRITICAL").length;
        const critDone = t.filter((x) => x.priority === "CRITICAL" && x.status === "DONE").length;
        return { pct: t.length ? (done / t.length) * 100 : 0, done, total: t.length, critDone, critTotal };
    }, [run]);

    const breachedSRs = openSRs.filter((s) => (s.breach_level || "").toUpperCase() === "RED" || s.overdue).length;

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="fh-dashboard">
            <GreetingHeader user={user} subtitle={`Managing ${site.name} · ${site.site_type || "MANAGED_CLIENT"}`} />
            <WhiteboardBanner />
            <ActionsNow />

            {/* Today's checklist */}
            <div className="fx-card fx-accent-stripe overflow-hidden" style={{ "--accent": accent }} data-testid="fh-checklist-card">
                <div className="p-5 pl-6 flex flex-wrap items-center gap-4 justify-between">
                    <div className="flex items-center gap-4">
                        <ProgressRing
                            value={progress.pct}
                            size={80}
                            strokeWidth={8}
                            accent={accent}
                            label={`${Math.round(progress.pct)}%`}
                            sublabel="today"
                        />
                        <div>
                            <div className="text-xs uppercase tracking-widest text-gray-500">Today's checklist</div>
                            <div className="font-display text-lg font-bold text-[#1E3355]">
                                {progress.done}/{progress.total} done
                            </div>
                            <div className="text-xs text-gray-500">CRITICAL {progress.critDone}/{progress.critTotal}{run?.locked ? " · signed off" : ""}</div>
                            {streak?.streak_days > 0 && <div className="mt-2"><StreakBadge days={streak.streak_days} best={streak.best_streak_days} testid="fh-streak" /></div>}
                        </div>
                    </div>
                    <Link
                        to={`/sites/${site.id}/run`}
                        className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-semibold hover:bg-[#15243C]"
                        data-testid="fh-open-run"
                    >
                        <ClipboardList className="w-4 h-4" /> Open checklist
                    </Link>
                </div>
            </div>

            {/* Summary cards row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryTile icon={Users}          label="Roster"             value={roster.length}            to={`/sites/${site.id}/attendance`} accent={accent} testid="fh-roster" />
                <SummaryTile icon={AlertTriangle}  label="Open service reqs"  value={openSRs.length}           to={`/sites/${site.id}/service-requests`} accent={accent} note={breachedSRs > 0 ? `${breachedSRs} breached` : undefined} noteRed testid="fh-srs" />
                <SummaryTile icon={ShoppingCart}   label="Submitted requisitions" value={pendingReq}          to={`/sites/${site.id}/requisition`} accent={accent} testid="fh-req" />
                <SummaryTile icon={CheckCircle2}   label="Streak"             value={streak?.streak_days || 0} to={`/sites/${site.id}/tracker`} accent={accent} note={streak?.best_streak_days ? `best ${streak.best_streak_days}` : undefined} testid="fh-streak-tile" />
            </div>

            {/* Quick actions */}
            <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Quick actions</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <QuickAction to={`/sites/${site.id}/attendance`}  icon={Users}          label="Attendance check-in"  accent={accent} testid="qa-attendance" />
                    <QuickAction to={`/sites/${site.id}/expenses`}    icon={Wallet}         label="Log expense"          accent={accent} testid="qa-expenses" />
                    <QuickAction to={`/sites/${site.id}/consumables`} icon={Package}        label="Consumables & stock"  accent={accent} testid="qa-consumables" />
                    <QuickAction to={`/sites/${site.id}/issue-log`}   icon={PackageMinus}   label="Daily issue log"      accent={accent} testid="qa-issuelog" />
                    <QuickAction to={`/sites/${site.id}/requisition`} icon={ShoppingCart}   label="Monthly requisition"  accent={accent} testid="qa-req" />
                    <QuickAction to={`/sites/${site.id}/diary`}       icon={Calendar}       label="Diary"                accent={accent} testid="qa-diary" />
                    <QuickAction to={`/chat`}                         icon={MessageSquare}  label="Team chat"            accent={accent} testid="qa-chat" />
                    <QuickAction to={`/kra`}                          icon={GraduationCap}  label="My KRA scorecard"     accent={accent} testid="qa-kra" />
                </div>
            </div>

            {/* Open service requests (top 5) */}
            {openSRs.length === 0 ? (
                <EmptyState icon="🎉" title="No open service requests" subtitle="All quiet at your site." testid="fh-no-srs" />
            ) : (
                <div className="fx-card p-4">
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Open service requests</div>
                    <ul className="space-y-2">
                        {openSRs.slice(0, 5).map((s) => (
                            <li key={s.id} className="flex items-start gap-2 text-sm">
                                <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${(s.breach_level || "").toUpperCase() === "RED" ? "text-[#E02B2B]" : "text-amber-500"}`} />
                                <span className="flex-1 line-clamp-2 text-gray-700">{s.description}</span>
                                <span className="text-[10px] text-gray-400">{fmtDate(s.created_at)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {sites.length > 1 && <OtherSitesLink sites={sites} currentId={site.id} />}
        </div>
    );
}

/* ---------------------- Own Building (FH) Dashboard ------------------------ */

function BuildingDashboard({ user, site, sites }) {
    const accent = site.accent_color || "#1E3355";
    const [petty, setPetty] = useState(null);
    const [expensesCount, setExpensesCount] = useState(null);
    const [rosterCount, setRosterCount] = useState(0);
    const month = new Date().toISOString().slice(0, 7);

    useEffect(() => {
        (async () => {
            await Promise.allSettled([
                api.get(`/buildings/${site.id}/petty`, { params: { month } }).then((r) => setPetty(r.data)).catch(() => {}),
                api.get(`/sites/${site.id}/expenses`, { params: { month } }).then((r) => setExpensesCount((r.data || []).length)).catch(() => {}),
                api.get(`/sites/${site.id}/roster`).then((r) => setRosterCount((r.data || []).length)).catch(() => {}),
            ]);
        })();
    }, [site.id, month]);

    // Petty response is either {balance,...} shape OR a list; be forgiving.
    const pettyBalance = Number.isFinite(parseFloat(petty?.balance)) ? parseFloat(petty.balance) : null;
    const pettyOpening = petty?.opening_float ?? petty?.opening ?? null;
    // Building considered "empty" only when there's genuinely nothing to show — no expenses,
    // no petty entries, no staff. Central/Centre demo sites hit this path.
    const pettyLinesCount = Array.isArray(petty?.lines) ? petty.lines.length : (petty?.lines_count ?? 0);
    const isEmpty = (expensesCount === 0) && (pettyBalance === null || pettyBalance === 0) && (pettyLinesCount === 0) && (rosterCount === 0);

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="fh-dashboard">
            <GreetingHeader user={user} subtitle={`Building operations · ${site.name}`} />
            <WhiteboardBanner />
            <ActionsNow />

            {isEmpty ? (
                <EmptyState
                    icon="🏢"
                    title="Registers are empty for this building"
                    subtitle="No expenses, salary rows or petty cash yet — start by adding your first entry."
                    action={<Link to={`/sites/${site.id}/expenses`} className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-semibold hover:bg-[#15243C]" data-testid="empty-add-expense">Add first expense</Link>}
                    testid="building-empty"
                />
            ) : (
                <div className="fx-card fx-accent-stripe overflow-hidden p-5 pl-6" style={{ "--accent": accent }} data-testid="building-petty-card">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-gray-500">Petty cash · {month}</div>
                            <div className="font-display text-2xl font-bold text-[#1E3355]" data-testid="building-petty-balance">
                                {pettyBalance !== null ? fmtInr(pettyBalance) : "—"}
                            </div>
                            {pettyOpening !== null && <div className="text-xs text-gray-500">Opening float: {fmtInr(pettyOpening)}</div>}
                        </div>
                        <Link to={`/buildings/${site.id}?tab=petty`} className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-semibold hover:bg-[#15243C]" data-testid="building-open-petty">
                            <Wallet className="w-4 h-4" /> Open petty cash
                        </Link>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <SummaryTile icon={Wallet} label="Expenses this month" value={expensesCount ?? "—"} to={`/sites/${site.id}/expenses`} accent={accent} testid="b-expenses" />
                <SummaryTile icon={Users}  label="Staff on roster"     value={rosterCount}            to={`/sites/${site.id}/attendance`} accent={accent} testid="b-roster" />
                <SummaryTile icon={Building2} label="Building hub"      value=""                       to={`/buildings/${site.id}`}       accent={accent} testid="b-hub" />
            </div>

            <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Quick actions</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <QuickAction to={`/sites/${site.id}/expenses`} icon={Wallet} label="Log an expense" accent={accent} testid="qa-expenses" />
                    <QuickAction to={`/buildings/${site.id}?tab=petty`} icon={Wallet} label="Add petty cash line" accent={accent} testid="qa-petty" />
                    <QuickAction to={`/buildings/${site.id}?tab=salary`} icon={Users} label="Salary register (masked)" accent={accent} testid="qa-salary" />
                    <QuickAction to={`/sites/${site.id}/attendance`} icon={Users} label="Attendance" accent={accent} testid="qa-attendance" />
                    <QuickAction to={`/chat`} icon={MessageSquare} label="Team chat" accent={accent} testid="qa-chat" />
                    <QuickAction to={`/kra`} icon={GraduationCap} label="My KRA scorecard" accent={accent} testid="qa-kra" />
                </div>
            </div>

            {sites.length > 1 && <OtherSitesLink sites={sites} currentId={site.id} />}
        </div>
    );
}

/* ---------------------- Coworking (FH) Dashboard -------------------------- */

function CoworkingDashboard({ user, site, sites }) {
    const accent = site.accent_color || "#1E3355";
    const today = new Date().toISOString().slice(0, 10);
    const month = today.slice(0, 7);
    const [salesToday, setSalesToday] = useState([]);
    const [snackStock, setSnackStock] = useState([]);
    const [printCount, setPrintCount] = useState(0);

    useEffect(() => {
        (async () => {
            await Promise.allSettled([
                api.get(`/coworking/${site.id}/sales`, { params: { date: today } }).then((r) => setSalesToday(r.data || [])).catch(() => {}),
                api.get(`/coworking/${site.id}/snacks/stock`, { params: { month } }).then((r) => setSnackStock(r.data || [])).catch(() => {}),
                api.get(`/coworking/${site.id}/print`, { params: { date: today } }).then((r) => setPrintCount((r.data || []).length)).catch(() => {}),
            ]);
        })();
    }, [site.id, today, month]);

    const totalToday = salesToday.reduce((a, s) => a + (Number(s.total_amount) || 0), 0);
    const lowStockItems = snackStock.filter((s) => (s.closing_stock ?? s.stock ?? 0) < 5);

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="fh-dashboard">
            <GreetingHeader user={user} subtitle={`Coworking · ${site.name}`} />
            <WhiteboardBanner />
            <ActionsNow />

            <div className="fx-card fx-accent-stripe overflow-hidden p-5 pl-6" style={{ "--accent": accent }} data-testid="cw-today-card">
                <div className="flex flex-wrap justify-between items-baseline gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-gray-500">My sales · {today}</div>
                        <div className="font-display text-2xl font-bold text-[#1E3355]" data-testid="cw-today-total">{fmtInr(totalToday)}</div>
                        <div className="text-xs text-gray-500">{salesToday.length} entries · {printCount} print/xerox logs</div>
                    </div>
                    <Link to={`/coworking/${site.id}?tab=sales`} className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-semibold hover:bg-[#15243C]" data-testid="cw-open-sales">
                        <Coffee className="w-4 h-4" /> Add sale
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <SummaryTile icon={Package}     label="Snack stock lines"  value={snackStock.length}   to={`/coworking/${site.id}?tab=snacks`} accent={accent} note={lowStockItems.length ? `${lowStockItems.length} low` : undefined} noteRed testid="cw-snack" />
                <SummaryTile icon={Printer}     label="Print/xerox today"  value={printCount}          to={`/coworking/${site.id}?tab=print`}  accent={accent} testid="cw-print" />
            </div>

            <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Quick actions</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <QuickAction to={`/coworking/${site.id}?tab=sales`}  icon={Coffee}  label="Sales entry"      accent={accent} testid="qa-sales" />
                    <QuickAction to={`/coworking/${site.id}?tab=snacks`} icon={Package} label="Snack stock"      accent={accent} testid="qa-snacks" />
                    <QuickAction to={`/coworking/${site.id}?tab=print`}  icon={Printer} label="Print/xerox log"  accent={accent} testid="qa-print" />
                    <QuickAction to={`/chat`}                             icon={MessageSquare} label="Team chat" accent={accent} testid="qa-chat" />
                </div>
            </div>

            {lowStockItems.length > 0 && (
                <div className="fx-card p-4 border-l-4" style={{ borderLeftColor: "#E02B2B" }}>
                    <div className="text-xs uppercase tracking-widest text-[#E02B2B] font-semibold mb-2">Low stock alert</div>
                    <ul className="text-sm text-gray-700 space-y-1">
                        {lowStockItems.slice(0, 6).map((s, i) => (
                            <li key={i} className="flex justify-between">
                                <span>{s.name || s.item_name}</span>
                                <span className="text-gray-500">{s.closing_stock ?? s.stock ?? 0} left</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {sites.length > 1 && <OtherSitesLink sites={sites} currentId={site.id} />}
        </div>
    );
}

/* ---------------------- Counter Staff Dashboard --------------------------- */

function CounterDashboard({ user, sites }) {
    if (!sites) return <DashboardSkeleton />;
    const site = (sites || []).find((s) => s.site_type === "COWORKING") || (sites || [])[0];
    if (!site) return <EmptyState icon="🔑" title="No coworking site assigned" />;
    return <Navigate to={`/coworking/${site.id}?tab=sales`} replace state={{ dashboard: "counter" }} />;
}

/* --------------------------- Assistant Dashboard --------------------------- */

function AssistantDashboard({ user, sites }) {
    const [kra, setKra] = useState(null);
    const [diary, setDiary] = useState([]);
    const [chatUnread, setChatUnread] = useState(0);
    const site = (sites || [])[0];
    const accent = site?.accent_color || "#1E3355";

    useEffect(() => {
        (async () => {
            await Promise.allSettled([
                api.get("/kra/me").then((r) => setKra(r.data)).catch(() => {}),
                (async () => {
                    if (site) {
                        const r = await api.get(`/sites/${site.id}/diary`, { params: { date: new Date().toISOString().slice(0, 10) } });
                        setDiary(r.data?.jobs || r.data || []);
                    }
                })().catch(() => {}),
                api.get("/chat/channels").then((r) => {
                    const chs = r.data || [];
                    setChatUnread(chs.reduce((n, c) => n + (c.unread || 0), 0));
                }).catch(() => {}),
            ]);
        })();
    }, [site?.id]);

    const done = diary.filter((j) => j.status === "DONE").length;
    const pending = diary.length - done;

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="assistant-dashboard">
            <GreetingHeader user={user} subtitle={site ? `My day at ${site.name}` : "My day"} />
            <WhiteboardBanner />
            <ActionsNow />

            <div className="fx-card fx-accent-stripe overflow-hidden p-5 pl-6" style={{ "--accent": accent }}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <ProgressRing
                            value={diary.length ? (done / diary.length) * 100 : 0}
                            size={80}
                            strokeWidth={8}
                            accent={accent}
                            label={`${done}/${diary.length}`}
                            sublabel="jobs"
                        />
                        <div>
                            <div className="text-xs uppercase tracking-widest text-gray-500">Today's diary</div>
                            <div className="font-display text-lg font-bold text-[#1E3355]">{pending ? `${pending} pending` : "All caught up 🎉"}</div>
                            <div className="text-xs text-gray-500">Assistant view · no financial data</div>
                        </div>
                    </div>
                    {site && (
                        <Link to={`/sites/${site.id}/diary`} className="fx-tap inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-semibold hover:bg-[#15243C]" data-testid="asst-open-diary">
                            <Calendar className="w-4 h-4" /> Open diary
                        </Link>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <SummaryTile icon={GraduationCap} label="My KRA" value={kra?.overall ?? "—"} to="/kra" accent={accent} testid="asst-kra" />
                <SummaryTile icon={MessageSquare} label="Chat unread" value={chatUnread} to="/chat" accent={accent} testid="asst-chat" />
            </div>

            <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Quick actions</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {site && <QuickAction to={`/sites/${site.id}/attendance`} icon={Users} label="Attendance check-in" accent={accent} testid="qa-attendance" />}
                    {site && <QuickAction to={`/sites/${site.id}/diary`} icon={Calendar} label="Today's diary jobs" accent={accent} testid="qa-diary" />}
                    <QuickAction to="/kra" icon={GraduationCap} label="My KRA scorecard" accent={accent} testid="qa-kra" />
                    <QuickAction to="/chat" icon={MessageSquare} label="Team chat" accent={accent} testid="qa-chat" />
                </div>
            </div>

            {diary.length === 0 && <EmptyState icon="🎉" title="No diary jobs yet" subtitle="Nothing scheduled for you today." testid="asst-no-diary" />}
        </div>
    );
}

/* --------------------------- Small reusable bits --------------------------- */

function SummaryTile({ icon: Icon, label, value, note, noteRed, to, accent, testid }) {
    const content = (
        <div className="fx-card fx-card-hover p-3.5 h-full" data-testid={testid}>
            <div className="flex items-center gap-2 text-gray-500">
                <Icon className="w-4 h-4" style={{ color: accent }} />
                <span className="text-[11px] uppercase tracking-widest">{label}</span>
            </div>
            <div className="mt-1 font-display text-xl font-bold text-[#1E3355]">{value ?? "—"}</div>
            {note && <div className={`text-[10px] mt-0.5 ${noteRed ? "text-[#E02B2B]" : "text-gray-400"}`}>{note}</div>}
        </div>
    );
    return to ? <Link to={to}>{content}</Link> : content;
}

function OtherSitesLink({ sites, currentId }) {
    const others = sites.filter((s) => s.id !== currentId);
    if (!others.length) return null;
    return (
        <div className="fx-card p-4">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Your other sites</div>
            <div className="flex flex-wrap gap-2">
                {others.map((s) => (
                    <Link key={s.id} to={`/sites/${s.id}/run`} className="text-xs px-2.5 py-1.5 rounded-full border border-gray-200 hover:bg-[#F4F6F9] text-[#1E3355]" style={{ borderColor: (s.accent_color || "#1E3355") + "40" }} data-testid={`other-site-${s.name}`}>
                        {s.name}
                    </Link>
                ))}
            </div>
        </div>
    );
}
